import log from '@/lib/logger';
import {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useAuth} from "@/context/use-auth";
import {Text} from "@/components/tailwind/text";
import {Textarea} from "@/components/tailwind/textarea";
import {Button} from "@/components/tailwind/button";
import ValidationAlert from "@/components/validation-alert";
import {Divider} from "@/components/tailwind/divider";
import {Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle} from "@/components/tailwind/dialog";
import {OrderDetails, OrderFormData} from "@/models/order";
import {MemberListItem} from "@/models/member";
import {ServiceGroup, ServiceWithGroupId} from "@/models/service";
import {generateCompleteMemberName, getMemberData, getMembers, getMembersWithService} from "@/services/member-services";
import {getListOfServiceGroups, getListOfServices} from "@/services/service-services";
import {Loader2, UserPlus, WrenchIcon, Info} from "lucide-react";
import MemberInfoCard from "@/components/member-info-card";
import {SubmissionChannel} from "@/models/submission-channel";
import {fetchAllSubmissionChannel} from "@/services/submission-channel-services";
import {useOrdersTranslation} from "@/i18n/hooks";
import {Select} from "@/components/tailwind/select";
import MemberSelectDialog from "@/components/member-select-dialog";
import SingleServiceSelector from "@/components/single-service-selector.tsx";
import {ProblemDetail} from "@/types/problem-details.ts";
import {isDefined} from "@/helpers/sanitizer.ts";
import {formatDateString} from "@/lib/utils";
import {z} from "zod";
import {updateOrder} from "@/services/order-services";
import {getOrderTimes} from "@/services/order-time-services";

const editOrderSchema = z.object({
    clientId: z.string().uuid("orderForm.edit.errors.client-id-invalid"),
    serviceId: z.string({error: "orderForm.edit.errors.service-not-selected"}).uuid("orderForm.edit.errors.invalid-service-uuid"),
    helperId: z.string().optional(),
    submissionChannelId: z.string().uuid("orderForm.edit.errors.submission-channel-id-invalid"),
    comments: z.string().optional()
});

type EditOrderFormData = z.infer<typeof editOrderSchema>;

interface OrderEditDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedOrder: OrderDetails) => void;
    orderData: OrderDetails;
}

export default function OrderEditDialog({isOpen, onClose, onSave, orderData}: OrderEditDialogProps) {
    const {t} = useOrdersTranslation();
    const {token} = useAuth();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<OrderFormData>({});

    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [helperDialogOpen, setHelperDialogOpen] = useState(false);

    const [members, setMembers] = useState<MemberListItem[]>([]);
    const [availableHelpers, setAvailableHelpers] = useState<MemberListItem[]>([]);
    const [services, setServices] = useState<ServiceWithGroupId[]>([]);
    const [serviceGroups, setServiceGroups] = useState<ServiceGroup[]>([]);
    const [submissionChannels, setSubmissionChannels] = useState<SubmissionChannel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAllDataAvailable, setIsAllDataAvailable] = useState(false);
    const [orderHasData, setOrderHasData] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        setValue,
        watch,
        setError,
        clearErrors,
        reset,
        formState: {errors}
    } = useForm<EditOrderFormData>({
        resolver: zodResolver(editOrderSchema),
    });

    const selectedClientId = watch("clientId");
    const selectedServiceId = watch("serviceId");
    const selectedHelperId = watch("helperId");

    // Initialize form with existing order data
    useEffect(() => {
        if (isOpen && orderData) {
            reset({
                clientId: orderData.client.id,
                serviceId: orderData.service.id,
                helperId: orderData.helper?.id || "",
                submissionChannelId: orderData.submissionChannel.id,
                comments: orderData.comments || ""
            });

            setFormData({
                client: orderData.client as any,
                helper: orderData.helper as any,
                service: orderData.service as any,
            });
            
            // Load order times to check if helper can be removed
            async function checkOrderData() {
                try {
                    const orderTimes = await getOrderTimes(token, orderData.id);
                    const hasOrderTimes = orderTimes && orderTimes.length > 0;
                    const hasMileage = orderData.mileage && orderData.mileage > 0;
                    const hasExpenses = orderData.reimbursableAmount && orderData.reimbursableAmount > 0;
                    setOrderHasData(!!(hasOrderTimes || hasMileage || hasExpenses));
                } catch (error) {
                    log.error("Error loading order times:", error);
                    // If we can't load order times, assume order has data to be safe
                    setOrderHasData(true);
                }
            }
            
            checkOrderData();
        }
    }, [isOpen, orderData, reset]);

    useEffect(() => {
        if (!isOpen) return;

        async function loadData() {
            setIsLoading(true);
            try {
                const [membersList, servicesList, serviceGroupsList, channels] = await Promise.all([
                    getMembers(token),
                    getListOfServices(token),
                    getListOfServiceGroups(token),
                    fetchAllSubmissionChannel(token),
                ]);

                setMembers(membersList || []);
                setAvailableHelpers(membersList || []);
                setServices(servicesList as ServiceWithGroupId[]);
                setServiceGroups(serviceGroupsList || []);
                setSubmissionChannels(channels || []);

                const hasMembers = isDefined<MemberListItem[]>(membersList) && membersList.length > 0;
                const hasServices = isDefined<ServiceWithGroupId[]>(servicesList) && servicesList.length > 0;
                const hasSubmissionChannels = isDefined<SubmissionChannel[]>(channels) && channels.length > 0;
                setIsAllDataAvailable(hasMembers && hasServices && hasSubmissionChannels);
            } catch (error) {
                log.error("Error loading information for order edit dialog", error);
                setIsAllDataAvailable(false);
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [isOpen, token]);

    useEffect(() => {
        async function updateClient() {
            if (selectedClientId) {
                const client = await getMemberData(token, selectedClientId);
                if (client)
                    setFormData(prev => ({...prev, client}));
            } else {
                setFormData(prev => ({...prev, client: undefined}));
            }
        }

        if (selectedClientId) {
            updateClient();
        }
    }, [selectedClientId, token]);

    useEffect(() => {
        async function updateServiceSelection() {
            if (selectedServiceId) {
                const service = services.find(s => s.id === selectedServiceId);
                setFormData(prev => ({...prev, service}));

                // Reset helper if service changes
                if (selectedServiceId !== orderData.service.id) {
                    setValue("helperId", "");
                    setFormData(prev => ({...prev, helper: undefined}));
                }

                try {
                    const helpers = await getMembersWithService(token, selectedServiceId);
                    const activeHelpers = (helpers || []).filter(m => m.status === "ACTIVE_MEMBER");
                    setAvailableHelpers(activeHelpers);

                    if (activeHelpers.length === 0) {
                        setError("helperId", {
                            type: "manual",
                            message: "orderForm.edit.errors.no-helpers-available"
                        });
                    } else {
                        clearErrors("helperId");
                    }
                } catch (error) {
                    log.error("Error loading available helpers:", error);
                    const activeMembers = members.filter(m => m.status === "ACTIVE_MEMBER");
                    setAvailableHelpers(activeMembers);

                    if (activeMembers.length === 0) {
                        setError("helperId", {
                            type: "manual",
                            message: "orderForm.edit.errors.no-active-members"
                        });
                    } else {
                        setError("helperId", {
                            type: "manual",
                            message: "orderForm.edit.errors.fallback-all-active-member"
                        });
                    }
                }
            } else {
                setFormData(prev => ({...prev, service: undefined}));
                setAvailableHelpers(members.filter(m => m.status === "ACTIVE_MEMBER"));
                clearErrors("helperId");
            }
        }

        if (selectedServiceId && services.length > 0) {
            updateServiceSelection();
        }
    }, [selectedServiceId, services, setValue, token, members, clearErrors, setError, orderData.service.id]);

    useEffect(() => {
        async function updateHelper() {
            if (selectedHelperId) {
                const helper = await getMemberData(token, selectedHelperId);
                if (helper)
                    setFormData(prev => ({...prev, helper}));

                if (selectedClientId === selectedHelperId) {
                    setError("helperId", {
                        type: "manual",
                        message: "orderForm.edit.errors.client-cannot-be-helper"
                    });
                } else {
                    clearErrors("helperId");
                }
            } else {
                setFormData(prev => ({...prev, helper: undefined}));
            }
        }

        if (selectedHelperId) {
            updateHelper();
        }
    }, [selectedHelperId, selectedClientId, token, setError, clearErrors]);

    const handleClientSelect = (client: MemberListItem) => {
        setValue("clientId", client.id);
        setClientDialogOpen(false);

        if (client.id === selectedHelperId) {
            setValue("helperId", "");
            setFormData(prev => ({...prev, helper: undefined}));
        }
    };

    const handleHelperSelect = (helper: MemberListItem) => {
        setValue("helperId", helper.id);
        setHelperDialogOpen(false);

        if (helper.id === selectedClientId) {
            setError("helperId", {
                type: "manual",
                message: "orderForm.edit.errors.client-cannot-be-helper"
            });
        } else {
            clearErrors("helperId");
        }
    };

    function sanitizeOrderData(data: EditOrderFormData): Partial<EditOrderFormData> {
        return {
            clientId: data.clientId,
            serviceId: data.serviceId,
            submissionChannelId: data.submissionChannelId,
            ...(data.helperId && data.helperId !== "" && {helperId: data.helperId}),
            ...(data.comments && data.comments.trim() !== "" && {comments: data.comments.trim()})
        };
    }

    async function onSubmit(data: EditOrderFormData) {
        if (data.clientId === data.helperId) {
            setError("helperId", {
                type: "manual",
                message: "orderForm.edit.errors.client-cannot-be-helper"
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const sanitizedData = sanitizeOrderData(data);
            const updatedOrder = await updateOrder(token, orderData.id, sanitizedData);

            // TODO what happens, when it is not set?
            if (updatedOrder) {
                onSave(updatedOrder);
            }

            onClose();
        } catch (error) {
            log.error("Error while updating the order:", error);
            setError("root", {
                type: "serverError",
                message: "orderForm.edit.errors.server-issue"
            });

            if ((error as ProblemDetail).validationIssues) {
                (error as ProblemDetail).validationIssues?.map(issue => {
                    setError(
                        `root.${issue.field}`, {
                            message: issue.message
                        }
                    );
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleClose = () => {
        reset();
        setFormData({});
        onClose();
    };

    return (
        <>
            <Dialog open={isOpen} onClose={handleClose} size="4xl" data-testid="edit-order-dialog">
                <DialogTitle>{t("orderForm.edit.heading")}</DialogTitle>
                <DialogDescription>
                    {t("orderForm.edit.description")}
                </DialogDescription>

                <DialogBody>
                    {isLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <Loader2 className="h-8 w-8 animate-spin text-primary"/>
                            <span className="ml-2">{t("orderForm.edit.loading")}</span>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            {/* Read-only Order Information */}
                            <section
                                className="grid gap-x-8 gap-y-4 sm:grid-cols-2 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                                <div>
                                    <Text className="font-medium text-gray-700 dark:text-gray-300">
                                        {t("orderForm.edit.orderNumber")}
                                    </Text>
                                    <Text className="text-lg font-semibold">{orderData.orderNo}</Text>
                                </div>
                                <div>
                                    <Text className="font-medium text-gray-700 dark:text-gray-300">
                                        {t("orderForm.edit.orderDate")}
                                    </Text>
                                    <Text className="text-lg">{formatDateString(orderData.orderDate)}</Text>
                                </div>
                            </section>

                            {(!isAllDataAvailable || errors.root) && (
                                <section aria-label={t("orderForm.edit.errors.ariaLabel.error-section")}>
                                    {!isAllDataAvailable && (
                                        <ValidationAlert message={t("orderForm.edit.errors.settings-missing")}/>
                                    )}

                                    {errors.root?.message && (
                                        <ValidationAlert message={t(errors.root.message)}/>
                                    )}
                                </section>
                            )}

                            <Divider soft/>

                            {/* Client Selection */}
                            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Text className="font-medium">
                                        {t("orderForm.edit.client.heading")}
                                    </Text>
                                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                                        {t("orderForm.edit.client.description")}
                                    </Text>
                                </div>
                                <div>
                                    <div className="flex gap-2 mb-2">
                                        <input hidden {...register("clientId")} />
                                        <Button
                                            type="button"
                                            onClick={() => setClientDialogOpen(true)}
                                            className="flex gap-2 items-center w-full justify-between"
                                            data-testid="order-client"
                                        >
                                            <span>
                                                {formData.client
                                                    ? generateCompleteMemberName(formData.client as any)
                                                    : t("orderForm.edit.client.button")}
                                            </span>
                                            <UserPlus className="h-5 w-5"/>
                                        </Button>
                                    </div>

                                    {errors.clientId && (
                                        <ValidationAlert
                                            message={t(errors.clientId.message || "orderForm.edit.errors.client-issue")}/>
                                    )}

                                    {formData.client && (
                                        <div className="mt-4">
                                            <MemberInfoCard member={formData.client} showPoints/>
                                        </div>
                                    )}
                                </div>
                            </section>

                            <Divider soft/>

                            {/* Service Selection */}
                            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Text className="font-medium">
                                        {t("orderForm.edit.service.heading")}
                                    </Text>
                                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                                        {t("orderForm.edit.service.description")}
                                    </Text>
                                </div>
                                <div>
                                    <Controller
                                        name="serviceId"
                                        control={control}
                                        render={({field}) => (
                                            <SingleServiceSelector
                                                services={services}
                                                groups={serviceGroups}
                                                selectedServiceId={field.value}
                                                onChange={(selected) => {
                                                    field.onChange(selected);
                                                }}
                                                className="w-full"
                                                placeholder={t("orderForm.edit.service.placeholder")}
                                                data-testid="order-service"
                                            />
                                        )}
                                    />
                                    {errors.serviceId && (
                                        <ValidationAlert
                                            message={t(errors.serviceId.message || "orderForm.edit.errors.invalid-service-uuid")}/>
                                    )}
                                </div>
                            </section>

                            <Divider soft/>

                            {/* Helper Selection */}
                            {selectedServiceId && (
                                <>
                                    <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                        <div className="space-y-1">
                                            <Text className="font-medium">
                                                {t("orderForm.edit.helper.heading")}
                                            </Text>
                                            <Text className="text-sm text-gray-600 dark:text-gray-400">
                                                {t("orderForm.edit.helper.description")}
                                            </Text>
                                        </div>
                                        <div>
                                            {!selectedHelperId && (
                                                <ValidationAlert
                                                    message={t("orderForm.edit.helper.warning")}
                                                    className="mb-2"
                                                />
                                            )}
                                            
                                            <div className="flex gap-2 items-center mb-2">
                                                <input hidden {...register("helperId")} />
                                                <Button
                                                    type="button"
                                                    onClick={() => setHelperDialogOpen(true)}
                                                    className="flex gap-2 items-center w-full justify-between"
                                                    disabled={availableHelpers.length === 0}
                                                    data-testid="order-helper"
                                                >
                                                    <span>
                                                        {formData.helper
                                                            ? generateCompleteMemberName(formData.helper as any)
                                                            : t("orderForm.edit.helper.button")}
                                                    </span>
                                                    <WrenchIcon className="h-5 w-5"/>
                                                </Button>

                                                {/* Remove Helper Button or Info Icon */}
                                                {formData.helper && (
                                                    orderHasData ? (
                                                        <div className="relative group flex items-center">
                                                            <Info 
                                                                className="h-5 w-5 text-gray-400 cursor-help" 
                                                                aria-label={t("orderForm.edit.helper.remove.disabledTooltip")}
                                                            />
                                                            <div className="absolute right-0 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                                                {t("orderForm.edit.helper.remove.disabledTooltip")}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            type="button"
                                                            plain
                                                            onClick={() => {
                                                                setValue("helperId", "");
                                                                setFormData(prev => ({...prev, helper: undefined}));
                                                                clearErrors("helperId");
                                                            }}
                                                            className="px-3"
                                                            aria-label={t("orderForm.edit.helper.remove.ariaLabel")}
                                                        >
                                                            ×
                                                        </Button>
                                                    )
                                                )}
                                            </div>

                                            {errors.helperId && (
                                                <ValidationAlert
                                                    message={t(errors.helperId.message || "orderForm.edit.errors.helper-issue")}/>
                                            )}

                                            {formData.helper && (
                                                <div className="mt-4">
                                                    <MemberInfoCard member={formData.helper}/>
                                                </div>
                                            )}
                                        </div>
                                    </section>

                                    <Divider soft/>
                                </>
                            )}

                            {/* Submission Channel */}
                            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Text className="font-medium">
                                        {t("orderForm.edit.submissionChannel.heading")}
                                    </Text>
                                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                                        {t("orderForm.edit.submissionChannel.description")}
                                    </Text>
                                </div>
                                <div>
                                    <Select
                                        data-testid="order-submission-channel"
                                        {...register("submissionChannelId")}
                                        invalid={!!errors.submissionChannelId}
                                    >
                                        {submissionChannels.map((channel) => (
                                            <option key={channel.id} value={channel.id}>
                                                {channel.name}
                                            </option>
                                        ))}
                                    </Select>
                                    {errors.submissionChannelId && (
                                        <ValidationAlert
                                            message={t(errors.submissionChannelId.message || "orderForm.edit.errors.submission-channel-issue")}/>
                                    )}
                                </div>
                            </section>

                            <Divider soft/>

                            {/* Comments */}
                            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Text className="font-medium">
                                        {t("orderForm.edit.comments.heading")}
                                    </Text>
                                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                                        {t("orderForm.edit.comments.description")}
                                    </Text>
                                </div>
                                <div>
                                    <Textarea
                                        data-testid="edit-order-comments"
                                        {...register("comments")}
                                        rows={4}
                                        placeholder={t("orderForm.edit.comments.placeholder")}
                                        invalid={!!errors.comments}
                                    />
                                    {errors.comments && (
                                        <ValidationAlert
                                            message={t(errors.comments.message || "orderForm.edit.errors.comments-issue")}/>
                                    )}
                                </div>
                            </section>
                        </form>
                    )}
                </DialogBody>

                <DialogActions>
                    <Button
                        type="button"
                        plain
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        {t("orderForm.edit.buttons.cancel")}
                    </Button>
                    <Button
                        type="submit"
                        onClick={handleSubmit(onSubmit)}
                        disabled={!isAllDataAvailable || isSubmitting || isLoading}
                        data-testid="save-order-changes-button"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                {t("orderForm.edit.buttons.saving")}
                            </>
                        ) : (
                            t("orderForm.edit.buttons.save")
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <MemberSelectDialog
                isOpen={clientDialogOpen}
                onClose={() => setClientDialogOpen(false)}
                onSelect={handleClientSelect}
                members={members}
                title={t("orderForm.edit.client.dialog-title")}
                excludeMemberId={selectedHelperId}
            />

            <MemberSelectDialog
                isOpen={helperDialogOpen}
                onClose={() => setHelperDialogOpen(false)}
                onSelect={handleHelperSelect}
                members={availableHelpers}
                title={t("orderForm.edit.helper.dialog-title")}
                excludeMemberId={selectedClientId}
            />
        </>
    );
}